/**
 * Darf Vespera das Mikrofon benutzen? — und was die Sprechtaste dann sagt.
 * (29.09.2026, Ticket 2643)
 *
 * ## Der Fall
 *
 * Konto 23 nimmt seit dem 24.09. nur digitale Stille auf: jede Sprechtaste
 * −91 dB, auch die NATIVE Tagesaufnahme desselben Geräts (18.09., 26.09.,
 * 28.09. — nachgemessen). Reine Nullen liefert iOS, wenn die App das
 * Mikrofon nicht benutzen darf. Bisher riet der Satz auf der Karte nur
 * („Bitte in den iPhone-Einstellungen … einschalten“) — ob das Recht fehlt,
 * wusste niemand.
 *
 * Ab Build 93 meldet die App das Recht selbst mit (`mikroRecht` im Ton-
 * zustand, AppDelegate.audioStateDict). Diese Datei ist die EINE Stelle, die
 * es liest und daraus den Satz macht. Ältere Builds und der Browser liefern
 * nichts — dann steht der Satz ohne behauptete Ursache da.
 *
 * ⚠️ Kein Ausweichen auf den nativen Aufnahmeweg: Bei Konto 23 war auch er
 *    stumm (siehe oben). Er würde das Diktat nicht retten.
 */
import { getAudioSessionState, oeffneAppEinstellungen as oeffneNativ } from "./native-recorder.js";

export type MikroRecht = "granted" | "denied" | "undetermined" | "unbekannt";

/** Das Recht aus dem nativen Tonzustand — alles andere ist „unbekannt“. */
export function mikroRechtAus(zustand: unknown): MikroRecht {
  const r = (zustand as { mikroRecht?: unknown } | null)?.mikroRecht;
  return r === "granted" || r === "denied" || r === "undetermined" ? r : "unbekannt";
}

/**
 * Der Satz bei einer Quelle, die nur Stille liefert. `inApp`: native App
 * (das Recht liegt in den iPhone-Einstellungen) oder Browser (bei der Seite).
 */
export function stummHinweisFuer(inApp: boolean, recht: MikroRecht): string {
  if (!inApp) {
    return "Das Mikrofon liefert nur Stille. Bitte in den Browser-Einstellungen das Mikrofon für diese Seite erlauben.";
  }
  if (recht === "denied" || recht === "undetermined") {
    return "Das Mikrofon ist für Vespera ausgeschaltet – in den iPhone-Einstellungen unter Vespera → Mikrofon einschalten.";
  }
  if (recht === "granted") {
    // Belegt ist nur: Das Recht ist da, der Ton nicht. Der Satz behauptet
    // keine Ursache, er sagt, was als Nächstes hilft.
    return "Das Mikrofon liefert nur Stille, obwohl Vespera es benutzen darf. Bitte Vespera ganz schließen und neu öffnen; hilft das nicht, das iPhone neu starten.";
  }
  return "Das Mikrofon liefert nur Stille. Bitte prüfe in den iPhone-Einstellungen unter Vespera, ob das Mikrofon eingeschaltet ist.";
}

/** Führt der Satz in die Einstellungen? Dann gehört der Knopf dazu. */
export function brauchtEinstellungen(recht: MikroRecht): boolean {
  return recht === "denied" || recht === "undetermined";
}

let letztes: MikroRecht = "unbekannt";

/** Das zuletzt gelesene Recht — für den Satz im Verlauf nach dem Ende. */
export function letztesMikroRecht(): MikroRecht {
  return letztes;
}

/** Fragt die App. Nie ein Fehler: ohne Antwort ist es „unbekannt“. */
export async function holeMikroRecht(): Promise<MikroRecht> {
  letztes = mikroRechtAus(await getAudioSessionState().catch(() => null));
  return letztes;
}

/** Öffnet die Einstellungen von Vespera (ab Build 93). false = geht hier nicht. */
export async function oeffneAppEinstellungen(): Promise<boolean> {
  return oeffneNativ();
}
